import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/auth.service";

type InviteUser = {
  email: string;
  firstName: string;
  lastName: string;
  passwordSet: boolean;
};

const InviteAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "loading" | "ready" | "invalid" | "success"
  >("loading");
  const [user, setUser] = useState<InviteUser | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const token = searchParams.get("token") || "";

  useEffect(() => {
    const verifyInvite = async () => {
      if (!token) {
        setStatus("invalid");
        setError("This invite link is missing a token.");
        return;
      }

      try {
        const response = await authService.verifyInvite(token);
        const inviteUser = response.user as InviteUser;

        if (inviteUser.passwordSet) {
          setStatus("invalid");
          setError("This invite has already been used. Please log in instead.");
          return;
        }

        setUser(inviteUser);
        setStatus("ready");
      } catch (err: any) {
        setStatus("invalid");
        setError(
          err.response?.data?.message ||
            "This invite link is invalid or expired."
        );
      }
    };

    verifyInvite();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await authService.acceptInvite(token, password);
      setStatus("success");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to set password.");
    }
  };

  const greeting = useMemo(() => {
    if (!user) return "Welcome";
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return fullName ? `Welcome, ${fullName}` : "Welcome";
  }, [user]);

  return (
    <div className="invite-page">
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&family=JetBrains+Mono:wght@400;600&display=swap');

        .invite-page {
          --ink: #0b1324;
          --muted: #506079;
          --accent: #2563eb;
          --accent-soft: rgba(37, 99, 235, 0.12);
          --bg: #e9eef5;
          --panel: #ffffff;
          --stroke: rgba(15, 23, 42, 0.08);
          font-family: 'Manrope', 'Segoe UI', sans-serif;
          min-height: calc(100vh - 120px);
          padding: 32px;
          border-radius: 28px;
          background:
            radial-gradient(circle at 15% 10%, rgba(37, 99, 235, 0.2), transparent 55%),
            radial-gradient(circle at 85% 80%, rgba(16, 185, 129, 0.15), transparent 60%),
            var(--bg);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .invite-card {
          width: min(520px, 100%);
          background: var(--panel);
          border-radius: 22px;
          padding: 32px;
          box-shadow: 0 20px 50px rgba(15, 23, 42, 0.15);
          border: 1px solid var(--stroke);
          display: grid;
          gap: 18px;
        }

        .invite-logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          text-align: center;
        }

        .invite-logo-image {
          width: 180px;
          height: auto;
          display: block;
        }

        .invite-logo-text {
          font-size: 1rem;
          font-weight: 700;
          color: var(--ink);
        }

        .invite-title {
          margin: 0;
          font-size: 2rem;
          color: var(--ink);
        }

        .invite-subtitle {
          margin: 0;
          color: var(--muted);
          line-height: 1.6;
        }

        .invite-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--accent-soft);
          color: var(--accent);
          padding: 2px 14px;
          border-radius: 999px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .invite-form {
          display: grid;
          gap: 14px;
        }

        .invite-input {
          width: 100%;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid var(--stroke);
          font-size: 1rem;
          font-family: inherit;
        }

        .invite-input-row {
          position: relative;
          display: flex;
          align-items: center;
        }

        .invite-input-row .invite-input {
          padding-right: 44px;
        }

        .invite-eye {
          position: absolute;
          right: 12px;
          background: transparent;
          border: none;
          color: var(--muted);
          cursor: pointer;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .invite-action {
          border: none;
          border-radius: 4px;
          padding: 12px 18px;
          font-weight: 700;
          font-size: 1rem;
          color: #ffffff;
          background: rgb(52, 152, 219);
          cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .invite-action:hover {
          transform: translateY(-1px);
        }

        .invite-meta {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.85rem;
          color: var(--muted);
          background: #f1f5f9;
          padding: 10px 12px;
          border-radius: 10px;
        }

        .invite-error {
          background: rgba(239, 68, 68, 0.15);
          color: #b91c1c;
          padding: 10px 12px;
          border-radius: 10px;
          font-size: 0.9rem;
        }

        .invite-success {
          background: rgba(16, 185, 129, 0.15);
          color: #047857;
          padding: 12px 14px;
          border-radius: 12px;
          font-size: 0.95rem;
        }

        .invite-link {
          border: none;
          background: transparent;
          color: var(--accent);
          text-decoration: underline;
          cursor: pointer;
          font-weight: 600;
          padding: 0;
        }
        `}
      </style>

      <div className="invite-card">
        <div className="invite-logo">
          <img
            className="invite-logo-image"
            src="https://eidosverse.com/assets/EidosGo.png"
            alt="EidosGo"
          />
          {/* <div className="invite-logo-text">EidosGo</div> */}
        </div>
        <div className="invite-tag"></div>
        <h1 className="invite-title">{greeting}</h1>
        <p className="invite-subtitle">
          Set a password to activate your account. You will use this password
          along with your email to sign in going forward.
        </p>

        {user && <div className="invite-meta">Account email: {user.email}</div>}

        {status === "loading" && (
          <p className="invite-subtitle">Verifying invite...</p>
        )}

        {status === "ready" && (
          <form className="invite-form" onSubmit={handleSubmit}>
            <div className="invite-input-row">
              <input
                className="invite-input"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              <button
                type="button"
                className="invite-eye"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <div className="invite-input-row">
              <input
                className="invite-input"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
              <button
                type="button"
                className="invite-eye"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            {error && <div className="invite-error">{error}</div>}
            <button type="submit" className="invite-action">
              Set password
            </button>
          </form>
        )}

        {status === "success" && (
          <>
            <div className="invite-success">
              Password set successfully. You can now log in.
            </div>
            <button
              type="button"
              className="invite-action"
              onClick={() => navigate("/login")}
            >
              Go to login
            </button>
          </>
        )}

        {status === "invalid" && (
          <>
            {error && <div className="invite-error">{error}</div>}
            <button
              type="button"
              className="invite-action"
              onClick={() => navigate("/login")}
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
